import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Users, Award, TrendingUp, CheckCircle, Star, ChevronDown, ChevronUp, Users2 } from "lucide-react";
import AskUsAnything from "@/components/AskUsAnything";
import AnimatedCounter from "@/components/AnimatedCounter";
import ClientCarousel from "@/components/ClientCarousel";

const HomePage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const stats = [
    { endValue: 125, suffix: "+", label: "Clients Served" },
    { endValue: 5, suffix: "+", label: "Years Experience" },
    { endValue: 99, suffix: "%", label: "Accuracy Rate" },
    { label: "Dedicated Client Support", isIcon: true },
  ];

  const services = [
    {
      icon: Award,
      title: "5+ Years of Expertise",
      description: "Proven track record in RERA compliance and real estate advisory with deep industry knowledge.",
    },
    {
      icon: null,
      customIcon: "team",
      title: "Expert Professional Team",
      description: "Dedicated team of legal experts, compliance specialists, and business consultants.",
    },
    {
      icon: null,
      customIcon: "support",
      title: "Dedicated Client Support",
      description: "Always available to address your queries and ensure seamless compliance.",
    },
  ];

  const testimonials = [
    {
      name: "Mr. Umesh Pawar",
      company: "Real Estate Developer",
      text: "RERA Hub made our project registration seamless. Their expertise and professionalism are unmatched. Highly recommended!",
      rating: 5,
    },
    {
      name: "Mrs. Priya",
      company: "Property Consultant",
      text: "Excellent service! The team handled all our RERA compliance needs efficiently. Very satisfied with their support.",
      rating: 5,
    },
    {
      name: "Mr. Mausam Lunkad",
      company: "Construction Firm",
      text: "Professional and reliable. RERA Hub simplified complex regulatory processes for our projects. Great experience!",
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: "What types of projects require RERA registration?",
      answer: "All residential and commercial real estate projects where the land area exceeds 500 sq meters or involves more than 8 apartments must be registered under RERA before marketing or selling.",
    },
    {
      question: "Can a project be marketed before RERA registration?",
      answer: "No. As per RERA regulations, no project can be advertised, marketed, booked, or sold without obtaining RERA registration. Violation can lead to penalties.",
    },
    {
      question: "What documents are required for RERA registration?",
      answer: "Key documents include land title documents, approved building plans, environmental clearances, no objection certificates, proforma of agreements, and financial statements.",
    },
    {
      question: "What is QPR (Quarterly Progress Report)?",
      answer: "QPR is a mandatory quarterly report submitted by developers to RERA authorities, detailing project progress including construction status, fund utilization, and compliance updates.",
    },
    {
      question: "What is the penalty for non-registration under RERA?",
      answer: "Promoters can face imprisonment up to 3 years and/or a fine up to 10% of the project cost. Additionally, project marketing and sales are prohibited without registration.",
    },
    {
      question: "What is an Agreement for Sale?",
      answer: "An Agreement for Sale is a legal document between buyer and seller outlining the terms of the property transaction, including price, payment schedule, and possession date.",
    },
    {
      question: "What is the defect liability period?",
      answer: "The defect liability period is typically 5 years from possession, during which the developer is responsible for fixing any structural defects or issues in the property.",
    },
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen fade-in" data-testid="home-page">
      {/* Hero Section */}
      <section
        className="hero-section relative min-h-[85vh] bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.2)), url('https://static.prod-images.emergentagent.com/jobs/e3ae400f-ac43-4e71-92d0-4fd17486a503/images/fb4e6eb8daa5422e29bc647b4f5d12b5766d81464707473dc571e2b77cb4fba9.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-20 flex items-center min-h-[85vh]">
          <div className="max-w-4xl">
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight text-[#1A1A1A] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="hero-heading"
            >
              RERA Filing Made Easy With
              <br />
              <span className="text-[#C9A14A]">RERA Hub</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#555555] mb-8" data-testid="hero-description">
              Your trusted partner for RERA registration, legal compliance, and business advisory services. We make complex processes simple.
            </p>
            <button
              onClick={() => navigate("/contact")}
              className="btn-accent px-8 py-4 rounded-md font-medium text-lg"
              data-testid="hero-cta-button"
            >
              Connect With Us
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 section-light" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-end">
            {stats.map((stat, index) => (
              <div key={index} className="text-center flex flex-col items-center justify-end" data-testid={`stat-item-${index}`}>
                {stat.isIcon ? (
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden bg-white shadow-md border-2 border-[#C9A14A] flex items-center justify-center mb-2">
                    <svg className="w-8 h-8 md:w-10 md:h-10 text-[#C9A14A]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                ) : (
                  <div
                    className="text-4xl md:text-5xl font-bold text-[#C9A14A] mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    <AnimatedCounter 
                      end={stat.endValue} 
                      duration={2000} 
                      suffix={stat.suffix}
                    />
                  </div>
                )}
                <div className="text-[#555555] font-medium whitespace-nowrap text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 md:py-32 section-grey" data-testid="founder-section-home">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Founder Image */}
            <div className="flex justify-center order-2 md:order-1">
              <div className="w-64 h-64 md:w-80 md:h-96 rounded-full overflow-hidden bg-white shadow-lg border-2 border-[#1A1A1A]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_legal-advisory-pro/artifacts/abg43a19_tejas%20dike.jpeg"
                  alt="Tejas Dike - Founder & CEO"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Founder Content */}
            <div className="order-1 md:order-2">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C9A14A] mb-4">
                Meet Our Founder
              </div>
              <h2
                className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Tejas Dike
              </h2>
              <p className="text-base text-[#C9A14A] font-semibold mb-6">
                Founder & CEO
              </p>
              <p className="text-base leading-relaxed text-[#555555] mb-4">
                Tejas Dike is a dedicated RERA and real estate compliance specialist with over 5 years of practical experience in guiding developers, agents, and property buyers through India's complex regulatory landscape.
              </p>
              <p className="text-base leading-relaxed text-[#555555] mb-4">
                He has successfully handled multiple RERA registrations, compliance filings, corrections, extensions, and advisory matters ensuring projects remain fully compliant, risk-free, and aligned with legal requirements.
              </p>
              <p className="text-base leading-relaxed text-[#555555] mb-6">
                With in depth expertise in the Real Estate (Regulation and Development) Act, 2016, Tejas founded RERA Hub with a clear mission: to simplify compliance, eliminate delays, and deliver transparent, efficient, and reliable consultancy services to the real estate sector.
              </p>
              
              {/* Key Highlights */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-3">Key Highlights</h3>
                <ul className="space-y-2 text-[#555555]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#C9A14A] font-bold">✔</span>
                    <span>Successfully assisted in 479+ RERA Registrations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C9A14A] font-bold">✔</span>
                    <span>Extensive experience with developers across Maharashtra</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C9A14A] font-bold">✔</span>
                    <span>Strong expertise in RERA Compliance, Documentation & Legal Advisory</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#C9A14A] font-bold">✔</span>
                    <span>Proven track record in QPR Filing, Corrections & Project Extensions</span>
                  </li>
                </ul>
              </div>

              <a
                href="https://www.linkedin.com/company/rerahub"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#C9A14A] hover:text-[#DFB65A] transition-colors duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span className="font-medium">Connect on LinkedIn</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-32 section-light" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="services-heading"
            >
              Comprehensive Business Solutions for End-to-End RERA Compliance & Advisory
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="service-card bg-white p-8 md:p-10 rounded-lg shadow-sm border border-slate-100"
                  data-testid={`service-card-${index}`}
                >
                  {service.customIcon === "support" ? (
                    <div className="w-12 h-12 mb-6 rounded-full border-2 border-[#C9A14A] flex items-center justify-center">
                      <svg className="w-7 h-7 text-[#C9A14A]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                  ) : service.customIcon === "team" ? (
                    <svg className="w-12 h-12 text-[#C9A14A] mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <Icon className="w-12 h-12 text-[#C9A14A] mb-6" />
                  )}
                  <h3 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {service.title}
                  </h3>
                  <p className="text-base leading-relaxed text-[#555555]">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate("/services")}
              className="btn-primary px-8 py-4 rounded-md font-medium"
              data-testid="view-all-services-button"
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      {/* Ask Us Anything Section */}
      <AskUsAnything />

      {/* Our Clients Section - Carousel */}
      <section className="py-12 md:py-16 bg-[#F4F4F4]" data-testid="our-clients-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-8 md:mb-10">
            <h2
              className="text-2xl sm:text-3xl md:text-4xl tracking-tight leading-snug text-[#1A1A1A] mb-2 md:mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="our-clients-heading"
            >
              Our Clients
            </h2>
            <p className="text-sm md:text-base text-[#555555]">
              Trusted by leading developers across Maharashtra.
            </p>
          </div>

          {/* Client Logos Carousel */}
          <ClientCarousel />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32 section-light" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A]"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="testimonials-heading"
            >
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-8 md:p-10 rounded-lg shadow-sm border border-slate-100"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#C9A14A] text-[#C9A14A]" />
                  ))}
                </div>
                <p className="text-base leading-relaxed text-[#555555] mb-6">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-semibold text-[#1A1A1A]">{testimonial.name}</div>
                  <div className="text-sm text-[#777777]">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 md:py-32 section-grey border-t-4 border-[#C9A14A]" data-testid="cta-banner-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <div className="bg-white p-12 rounded-lg shadow-sm border border-slate-100">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="cta-heading"
            >
              Ready to Get Started?
            </h2>
            <p className="text-lg leading-relaxed text-[#555555] mb-8 max-w-2xl mx-auto" data-testid="cta-description">
              Let us help you navigate the complexities of RERA compliance and grow your business with confidence.
            </p>
            <button
              onClick={() => navigate("/contact")}
              className="btn-accent px-8 py-4 rounded-md font-medium text-lg"
              data-testid="cta-button"
            >
              Contact Us Today
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
